import type { Pillar, TrackerPillar, Week } from 'chery-contracts';
import { CalendarPlus, ChevronDown, Download, ExternalLink, MessageSquare, MessageSquareText, Save, Undo2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { cn } from '../../lib/cn';
import { PILLAR_META } from '../../lib/pillars';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { weekDateLabel } from './tracker-display';

export type TrackerRange = { mode: 'range'; fromWeekId: string; toWeekId: string } | { mode: 'all' };

export interface TrackerToolbarProps {
  allWeeks: readonly Week[];
  range: TrackerRange;
  shownWeekCount: number;
  onRangeChange: (fromWeekId: string, toWeekId: string) => void;
  onToggleAllWeeks: () => void;
  onYearToDate: () => void;

  pillars: readonly TrackerPillar[];
  pillarFilter: Pillar | '';
  onPillarFilter: (pillar: Pillar | '') => void;
  search: string;
  onSearch: (search: string) => void;
  showComments: boolean;
  onToggleComments: () => void;
  canComment: boolean;
  onComment: () => void;

  canAddWeek: boolean;
  isAddingWeek: boolean;
  onAddWeek: () => void;
  onExport: () => void;
  onOpenReports: () => void;

  /** False for a user with no EDIT pillar: no Undo / Cancel / Save at all. */
  canEdit: boolean;
  canUndo: boolean;
  unsavedCount: number;
  isSaving: boolean;
  onUndo: () => void;
  onCancel: () => void;
  onSave: () => void;
}

/** The small "Actions" menu: add a reporting week (ADMIN/PM), export CSV, open Reports. */
const ActionsMenu = ({ items }: { items: { key: string; label: string; icon: typeof Download; onSelect: () => void; disabled?: boolean }[] }) => {
  const { t } = useI18nTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onPointerDown = (event: MouseEvent): void => {
      if (root.current && event.target instanceof Node && !root.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);

    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [isOpen]);

  return (
    <div
      ref={root}
      className="relative"
    >
      <Button
        variant="outline"
        size="sm"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
      >
        {t('tracker.actions.menu')}
        <ChevronDown className="size-3.5" />
      </Button>
      {isOpen ? (
        <div
          role="menu"
          className="absolute end-0 top-9 z-40 min-w-[13rem] rounded-md border border-border bg-card p-1 shadow-lg"
        >
          {items.map(({ key, label, icon: Icon, onSelect, disabled }) => (
            <button
              key={key}
              type="button"
              role="menuitem"
              disabled={disabled}
              onClick={() => {
                setIsOpen(false);
                onSelect();
              }}
              className="flex w-full items-center gap-2 rounded px-2.5 py-2 text-start text-sm text-card-foreground hover:bg-muted disabled:opacity-50"
            >
              <Icon className="size-4 text-muted-foreground" />
              {label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

/**
 * Tracker toolbar (plan §7): From/To week selects, All weeks, Year to date, pillar filter, search, comments
 * toggle, the Actions menu, and Undo · Cancel · Save (n) for users who may edit.
 */
export const TrackerToolbar = (props: TrackerToolbarProps) => {
  const { t } = useI18nTranslation();
  const weekOptions = props.allWeeks.map((week) => ({
    value: week.id,
    label: `${weekDateLabel(week.weekDate)} · W${String(week.weekNumber).padStart(2, '0')}`,
  }));
  const pillarOptions = [
    { value: '', label: t('tracker.allPillars') },
    ...props.pillars.map(({ pillar }) => ({ value: pillar, label: PILLAR_META[pillar].label })),
  ];
  const first = props.allWeeks[0];
  const last = props.allWeeks[props.allWeeks.length - 1];

  const actions = [
    ...(props.canAddWeek
      ? [{ key: 'add-week', label: t('tracker.actions.addWeek'), icon: CalendarPlus, onSelect: props.onAddWeek, disabled: props.isAddingWeek }]
      : []),
    { key: 'export', label: t('tracker.actions.export'), icon: Download, onSelect: props.onExport },
    { key: 'reports', label: t('tracker.actions.openReports'), icon: ExternalLink, onSelect: props.onOpenReports },
  ];

  return (
    <div className="space-y-2 border-b border-border bg-card px-4 py-3">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-lg font-semibold text-foreground">{t('pages.tracker')}</h1>

        {props.range.mode === 'all' ? (
          <span className="rounded-md border border-border bg-muted px-2.5 py-1.5 text-xs font-medium text-foreground">
            {t('tracker.allWeeksRange', { from: first ? weekDateLabel(first.weekDate) : '—', to: last ? weekDateLabel(last.weekDate) : '—' })}
          </span>
        ) : (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <label
              htmlFor="tracker-from-week"
              className="font-medium"
            >
              {t('tracker.from')}
            </label>
            <Select
              id="tracker-from-week"
              value={props.range.fromWeekId}
              options={weekOptions}
              onChange={(event) => props.range.mode === 'range' && props.onRangeChange(event.target.value, props.range.toWeekId)}
              className="h-8 w-44 text-xs"
            />
            <span aria-hidden>→</span>
            <label
              htmlFor="tracker-to-week"
              className="font-medium"
            >
              {t('tracker.to')}
            </label>
            <Select
              id="tracker-to-week"
              value={props.range.toWeekId}
              options={weekOptions}
              onChange={(event) => props.range.mode === 'range' && props.onRangeChange(props.range.fromWeekId, event.target.value)}
              className="h-8 w-44 text-xs"
            />
          </div>
        )}
        <span className="text-xs text-muted-foreground">{t('tracker.weekCount', { count: props.shownWeekCount })}</span>

        <div className="ms-auto flex items-center gap-2">
          <ActionsMenu items={actions} />
          {props.canEdit ? (
            <>
              <Button
                variant="outline"
                size="sm"
                disabled={!props.canUndo || props.isSaving}
                onClick={props.onUndo}
                title={t('tracker.undoHint')}
              >
                <Undo2 className="size-3.5" />
                {t('tracker.undo')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={props.unsavedCount === 0 || props.isSaving}
                onClick={props.onCancel}
              >
                <X className="size-3.5" />
                {t('actions.cancel')}
              </Button>
              <Button
                size="sm"
                isLoading={props.isSaving}
                disabled={props.unsavedCount === 0 || props.isSaving}
                onClick={props.onSave}
              >
                {props.isSaving ? null : <Save className="size-3.5" />}
                {props.unsavedCount > 0 ? t('tracker.saveCount', { count: props.unsavedCount }) : t('actions.save')}
              </Button>
            </>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="search"
          value={props.search}
          onChange={(event) => props.onSearch(event.target.value)}
          placeholder={t('tracker.search')}
          aria-label={t('tracker.search')}
          className="h-8 w-56 text-xs"
        />
        <Select
          value={props.pillarFilter}
          options={pillarOptions}
          onChange={(event) => props.onPillarFilter(event.target.value as Pillar | '')}
          aria-label={t('tracker.pillarFilter')}
          className="h-8 w-52 text-xs"
        />
        <Button
          variant="outline"
          size="sm"
          aria-pressed={props.range.mode === 'all'}
          onClick={props.onToggleAllWeeks}
          className={cn(props.range.mode === 'all' && 'bg-muted font-semibold')}
        >
          {t('tracker.allWeeks')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          aria-pressed={props.showComments}
          onClick={props.onToggleComments}
          className={cn(props.showComments && 'bg-muted')}
        >
          <MessageSquare className="size-3.5" />
          {props.showComments ? t('tracker.commentsShown') : t('tracker.commentsHidden')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={!props.canComment}
          onClick={props.onComment}
        >
          <MessageSquareText className="size-3.5" />
          {t('tracker.comment')}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={props.onYearToDate}
          className="ms-auto"
          title={t('tracker.yearToDateHint')}
        >
          {t('tracker.yearToDate')}
        </Button>
      </div>
    </div>
  );
};
